/**
 * Checkbox Component
 *
 * A styled checkbox component with label support.
 */

import { forwardRef, type InputHTMLAttributes, useId } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { Check, Minus } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  indeterminate?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

// ============================================================================
// Styles
// ============================================================================

const sizeStyles = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

const iconSizeStyles = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

// ============================================================================
// Component
// ============================================================================

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      label,
      description,
      indeterminate = false,
      size = 'md',
      id,
      checked,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const checkboxId = id || generatedId;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      Logger.debug(LogCategory.UI, 'Checkbox changed', 'Checkbox', {
        checkboxId,
        checked: e.target.checked,
        name: props.name,
      });
      onChange?.(e);
    };

    const isChecked = checked || false;

    return (
      <div className="flex items-start gap-3">
        <label htmlFor={checkboxId} className="relative flex items-center cursor-pointer">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            checked={checked}
            onChange={handleChange}
            className="sr-only peer"
            {...props}
          />
          <div
            className={cn(
              'rounded border-2 transition-all duration-150',
              'flex items-center justify-center pointer-events-none',
              sizeStyles[size],
              isChecked || indeterminate
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'border-slate-300 bg-white',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2',
              'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
              !isChecked && !indeterminate && 'hover:border-slate-400',
              className
            )}
          >
            {indeterminate ? (
              <Minus className={iconSizeStyles[size]} strokeWidth={3} />
            ) : isChecked ? (
              <Check className={iconSizeStyles[size]} strokeWidth={3} />
            ) : null}
          </div>
        </label>

        {(label || description) && (
          <div className="flex-1 min-w-0">
            {label && (
              <label
                htmlFor={checkboxId}
                className="text-sm font-medium text-slate-700 cursor-pointer"
              >
                {label}
              </label>
            )}
            {description && (
              <p className="mt-0.5 text-sm text-slate-500">{description}</p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
